"use client";

import clsx from "clsx";
import Link from "next/link";
import Icon, {ICON_SIZES, IconNameExtended} from "../library/Icon";
import {Card} from "../ui/card";

type JumpInCardTarget =
   | {href: string; onOpenSideChat?: never}
   /** Opens the side chat in place. */
   | {href?: never; onOpenSideChat: () => void};

type JumpInCardProps = JumpInCardTarget & {
   icon: IconNameExtended;
   title: string;
   description: string;
   iconClassName?: string;
   iconBgClassName?: string;
   hoverBorderClassName?: string;
};

export default function JumpInCard({
   icon,
   title,
   description,
   href,
   onOpenSideChat,
   iconClassName,
   iconBgClassName,
   hoverBorderClassName,
}: Readonly<JumpInCardProps>) {
   const opensSideChat = Boolean(onOpenSideChat);
   const cardClassName = clsx(
      "h-full shadow-none cursor-pointer transition-[transform,border-color,box-shadow] duration-150",
      opensSideChat
         ? "hover:border-[#5B9AF7] hover:shadow-[0_4px_14px_rgb(47_123_255/0.12)] focus-visible:border-[#5B9AF7] active:bg-200/30"
         : hoverBorderClassName
   );
   // Button descendants must be phrasing content; link cards retain their heading semantics.
   const Content = opensSideChat ? "span" : "div";
   const Title = opensSideChat ? "span" : "h3";
   const Description = opensSideChat ? "span" : "p";
   const content = (
      <Content className="block p-4">
         <span className="mb-4 flex">
            <span
               className={clsx(
                  "w-12",
                  "h-12",
                  "rounded-full",
                  iconBgClassName ?? "bg-panel_secondary",
                  "flex",
                  "items-center",
                  "justify-center"
               )}
            >
               <Icon icon={icon} size={ICON_SIZES.LG} className={iconClassName} />
            </span>
         </span>
         <Title className="block font-semibold text-900 mb-1">{title}</Title>
         <Description className="block text-sm text-500">{description}</Description>
      </Content>
   );

   if (onOpenSideChat) {
      return (
         <button
            type="button"
            onClick={onOpenSideChat}
            className={clsx(
               cardClassName,
               "group flex w-full flex-col rounded-xl border border-neutral bg-panel text-card-foreground text-left outline-none ease-out motion-safe:active:scale-[0.98] active:duration-100 focus-visible:ring-2 focus-visible:ring-gailPrimary/40 focus-visible:transition-none motion-reduce:transition-none"
            )}
         >
            {content}
         </button>
      );
   }

   return (
      <Link href={href} className={clsx("block", "group")}>
         <Card className={cardClassName}>{content}</Card>
      </Link>
   );
}
